import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import DownloadIcon from '@mui/icons-material/Download';
import { useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  orchestratorAuthApi,
  type MfaSetupResponseDto,
} from '../api/orchestratorAuth';
import {
  messageFromMfaCompletionError,
  messageFromOrchestratorAxiosError,
} from '../lib/axiosErrorMessage';
import { completeOperatorLogin } from '../lib/completeOperatorLogin';
import { terminateOperatorSession } from '../lib/adminSession';
import { normalizeAdminMfaRecoveryCode } from '../lib/mfaChallengeSession';
import {
  clearMfaLoginCredentials,
  resolveMfaLoginCredentials,
  writeMfaLoginCredentials,
} from '../lib/mfaLoginCredentials';
import {
  readRememberDevicePreference,
  writeRememberDevicePreference,
} from '../lib/mfaRememberDevice';
import { resolvePostLoginPath } from '../config/paths';
import { GEMINI_GRADIENT } from '../theme';

type MfaMode = 'enroll' | 'challenge' | 'recovery' | 'codes';

interface MfaLocationState {
  email?: string;
  password?: string;
  displayName?: string;
  returnTo?: string;
  recoveryCodes?: string[];
}

function currentMode(pathname: string): MfaMode {
  if (pathname.includes('/recovery-codes')) return 'codes';
  if (pathname.includes('/recovery')) return 'recovery';
  if (pathname.includes('/challenge')) return 'challenge';
  return 'enroll';
}

function normalizeCode(value: string) {
  return value.replace(/\D/g, '').slice(0, 6);
}

export function downloadRecoveryCodes(codes: string[]) {
  const body = [
    'Cash4You Admin Console — MFA Recovery Codes',
    '',
    'Store these in a safe place. Each code can be used once if you lose access to your authenticator app.',
    'Enter codes exactly as shown (dashes optional).',
    '',
    ...codes,
    '',
  ].join('\n');
  const blob = new Blob([body], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'cash4you-admin-recovery-codes.txt';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function mfaCompletionErrorMessage(err: unknown): string {
  return (
    messageFromMfaCompletionError(err) ||
    'Could not complete admin console sign-in. Please try signing in again.'
  );
}

function totpErrorMessage(err: unknown): string {
  const message = messageFromMfaCompletionError(err);
  const lower = message.toLowerCase();
  if (lower.includes('challenge expired') || lower.includes('sign in again')) {
    return 'Your MFA session expired. Please sign in again, then enter a fresh authenticator code.';
  }
  if (lower.includes('authentication required')) {
    return 'Your sign-in session was lost before MFA could complete. Please sign in again.';
  }
  if (lower.includes('totp_clock_skew') || (lower.includes('clock') && lower.includes('skew'))) {
    return 'Your authenticator code looks valid but your device clock may be out of sync. Enable automatic date & time on your phone or PC, wait for the next code, and try again.';
  }
  if (lower.includes('invalid totp') || lower.includes('invalid code')) {
    return 'That code is invalid or has expired. Enter the current 6-digit code from your authenticator app.';
  }
  return message || 'We could not verify that code. Please try again.';
}

export function MfaPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const mode = currentMode(location.pathname);
  const locationState = (location.state as MfaLocationState | null) ?? {};
  const resolvedCredentials = useMemo(
    () => resolveMfaLoginCredentials(locationState),
    [locationState],
  );

  const credentials = useMemo(
    () => ({
      email: resolvedCredentials?.email?.trim() ?? locationState.email?.trim() ?? '',
      password: resolvedCredentials?.password ?? locationState.password ?? '',
      displayName: resolvedCredentials?.displayName ?? locationState.displayName,
      returnTo: resolvePostLoginPath(
        resolvedCredentials?.returnTo ?? locationState.returnTo ?? '/',
      ),
    }),
    [
      locationState.displayName,
      locationState.email,
      locationState.password,
      locationState.returnTo,
      resolvedCredentials?.displayName,
      resolvedCredentials?.email,
      resolvedCredentials?.password,
      resolvedCredentials?.returnTo,
    ],
  );

  const [loading, setLoading] = useState(mode === 'enroll');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(readRememberDevicePreference);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [setup, setSetup] = useState<MfaSetupResponseDto | null>(null);
  const recoveryCodes = locationState.recoveryCodes ?? [];
  const [sessionMfaEnabled, setSessionMfaEnabled] = useState(false);
  const [mfaStepVerified, setMfaStepVerified] = useState(false);
  const setupRequestedRef = useRef(false);

  const requiresCredentials = mode !== 'codes' || !recoveryCodes.length;

  useEffect(() => {
    if (credentials.email && credentials.password) {
      writeMfaLoginCredentials({
        email: credentials.email,
        password: credentials.password,
        displayName: credentials.displayName,
        returnTo: credentials.returnTo,
      });
    }
  }, [credentials.displayName, credentials.email, credentials.password, credentials.returnTo]);

  useEffect(() => {
    if (requiresCredentials && (!credentials.email || !credentials.password)) {
      void navigate('/login', { replace: true });
    }
  }, [credentials.email, credentials.password, navigate, requiresCredentials]);

  useEffect(() => {
    let active = true;

    const load = async () => {
      if (mode === 'codes') {
        setLoading(false);
        return;
      }

      if (mode === 'challenge' || mode === 'recovery') {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      try {
        const session = await orchestratorAuthApi.me();
        if (!active) return;

        if (!session.authenticated && mode === 'enroll') {
          setError('Your sign-in session expired. Please sign in again.');
          return;
        }

        setSessionMfaEnabled(Boolean(session.mfaEnabled));

        if (mode === 'enroll' && !session.mfaEnabled && !setupRequestedRef.current) {
          setupRequestedRef.current = true;
          const response = await orchestratorAuthApi.setupMfa();
          if (!active) return;
          setSetup(response);
        }
      } catch (err) {
        if (!active) return;
        setError(messageFromOrchestratorAxiosError(err) || 'Could not load MFA setup.');
      } finally {
        if (active) setLoading(false);
      }
    };

    void load();
    return () => {
      active = false;
    };
  }, [mode]);

  const completeAdminConsoleSignIn = async (commitOrchestratorMfa: boolean) => {
    // Promote pendingMfaAuth → authSession before admin-api provisioning so
    // orchestrator /auth/me is fully authenticated and pending state is not
    // lost if ensureOperatorAccount is slow or fails mid-flight.
    if (commitOrchestratorMfa) {
      const commit = await orchestratorAuthApi.commitMfaLogin();
      if (commit.success !== true) {
        throw new Error(
          commit.message?.trim() ||
            'MFA was accepted but sign-in could not be completed. Try Continue below or sign in again.',
        );
      }
    }
    await completeOperatorLogin(qc, {
      email: credentials.email,
      password: credentials.password,
      displayName: credentials.displayName,
    });
    clearMfaLoginCredentials();
    void navigate(credentials.returnTo, { replace: true });
  };

  const finishLogin = async () => {
    await completeAdminConsoleSignIn(false);
  };

  const finishMfaChallengeLogin = async () => {
    await completeAdminConsoleSignIn(true);
  };

  const redirectToSignInAfterLostChallenge = async (message: string) => {
    setError(message);
    await terminateOperatorSession(qc);
    void navigate('/login', { replace: true });
  };

  const handleVerifyChallenge = async () => {
    if (totpCode.length !== 6) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setSubmitting(true);
    setError('');
    let challengeAccepted = false;
    try {
      const result = await orchestratorAuthApi.verifyMfa(totpCode, rememberDevice);
      if (result.success !== true) {
        const msg = totpErrorMessage({ response: { data: result } });
        if (/challenge expired|authentication required|sign in again/i.test(msg)) {
          await redirectToSignInAfterLostChallenge(msg);
          return;
        }
        setError(msg);
        return;
      }
      challengeAccepted = true;
      setMfaStepVerified(true);
      await finishMfaChallengeLogin();
    } catch (err) {
      const completionMsg = mfaCompletionErrorMessage(err);
      if (/challenge expired|authentication required|sign in again/i.test(completionMsg)) {
        await redirectToSignInAfterLostChallenge(totpErrorMessage(err));
      } else if (/invalid totp|invalid code|invalid recovery/i.test(completionMsg.toLowerCase())) {
        setError(totpErrorMessage(err));
      } else if (challengeAccepted || mfaStepVerified) {
        setError(
          completionMsg ||
            'MFA was accepted but the admin console could not be opened. Use Continue below to retry without entering a new code.',
        );
      } else {
        setError(completionMsg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyRecovery = async () => {
    const normalized = normalizeAdminMfaRecoveryCode(recoveryCode);
    if (!normalized) {
      setError('Enter a recovery code.');
      return;
    }
    setSubmitting(true);
    setError('');
    let challengeAccepted = false;
    try {
      const result = await orchestratorAuthApi.verifyMfaRecoveryCode(normalized);
      if (result.success !== true) {
        const raw =
          typeof result.message === 'string' && result.message.trim()
            ? result.message.trim()
            : 'That recovery code is invalid or already used.';
        const msg = /challenge expired|authentication required/i.test(raw)
          ? totpErrorMessage({ response: { data: result } })
          : raw;
        if (/challenge expired|authentication required|sign in again/i.test(msg)) {
          await redirectToSignInAfterLostChallenge(msg);
          return;
        }
        setError(msg);
        return;
      }
      challengeAccepted = true;
      setMfaStepVerified(true);
      await finishMfaChallengeLogin();
    } catch (err) {
      const completionMsg = mfaCompletionErrorMessage(err);
      if (/challenge expired|authentication required|sign in again/i.test(completionMsg)) {
        await redirectToSignInAfterLostChallenge(totpErrorMessage(err));
      } else if (/invalid recovery|invalid code/i.test(completionMsg.toLowerCase())) {
        setError(completionMsg);
      } else if (challengeAccepted || mfaStepVerified) {
        setError(
          completionMsg ||
            'Recovery code was accepted but the admin console could not be opened. Use Continue below to retry without entering a new code.',
        );
      } else {
        setError(completionMsg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEnableMfa = async () => {
    if (totpCode.length !== 6) {
      setError('Enter the 6-digit code from your authenticator app to confirm setup.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const response = await orchestratorAuthApi.enableMfa(totpCode);
      void navigate('/mfa/recovery-codes', {
        replace: true,
        state: {
          ...locationState,
          recoveryCodes: response.recoveryCodes,
        },
      });
    } catch (err) {
      setError(totpErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinueFromCodes = async () => {
    setSubmitting(true);
    setError('');
    try {
      await finishLogin();
    } catch (err) {
      setError(
        mfaCompletionErrorMessage(err) ||
          'Could not complete admin console sign-in. Please try signing in again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToSignIn = async () => {
    await terminateOperatorSession(qc);
    void navigate('/login', { replace: true });
  };

  const header = useMemo(() => {
    switch (mode) {
      case 'challenge':
        return {
          title: 'Two-factor authentication',
          subtitle: 'Enter the 6-digit code from your authenticator app.',
        };
      case 'recovery':
        return {
          title: 'Use a recovery code',
          subtitle: 'Enter one of your single-use recovery codes.',
        };
      case 'codes':
        return {
          title: 'Save your recovery codes',
          subtitle: 'Download or copy these codes before continuing to the admin console.',
        };
      default:
        return {
          title: 'Set up authenticator MFA',
          subtitle: 'Multi-factor authentication is required for all admin operators.',
        };
    }
  }, [mode]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 480,
          width: 1,
          borderRadius: 4,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ height: 4, background: GEMINI_GRADIENT }} />

        <Box sx={{ p: 4, pt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '8px',
                background: GEMINI_GRADIENT,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SecurityIcon sx={{ color: '#fff', fontSize: 16 }} />
            </Box>
            <Typography variant="caption" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
              Admin console security
            </Typography>
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            {header.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {header.subtitle}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={40} thickness={3} sx={{ mb: 2, color: theme.palette.primary.main }} />
              <Typography variant="body2" color="text.secondary">
                Preparing MFA…
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2.5}>
              {mode === 'challenge' && (
                <>
                  <TextField
                    label="Authenticator code"
                    value={totpCode}
                    onChange={(event) => setTotpCode(normalizeCode(event.target.value))}
                    inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6 }}
                    fullWidth
                    autoFocus
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={rememberDevice}
                        onChange={(_, checked) => {
                          setRememberDevice(checked);
                          writeRememberDevicePreference(checked);
                        }}
                        disabled={submitting}
                      />
                    }
                    label="Remember this device"
                  />
                  <Button
                    variant="contained"
                    fullWidth
                    disabled={submitting || totpCode.length !== 6}
                    onClick={() => void handleVerifyChallenge()}
                    sx={{
                      py: 1.25,
                      fontWeight: 700,
                      background: GEMINI_GRADIENT,
                      '&:hover': { background: GEMINI_GRADIENT, opacity: 0.9 },
                    }}
                  >
                    {submitting ? 'Verifying…' : 'Verify and continue'}
                  </Button>
                  {mfaStepVerified && (
                    <Button
                      variant="outlined"
                      fullWidth
                      disabled={submitting}
                      onClick={() => {
                        setSubmitting(true);
                        setError('');
                        void finishMfaChallengeLogin().catch((err) => {
                          setError(
                            mfaCompletionErrorMessage(err) ||
                              'Could not complete admin console sign-in. Check that admin-api is running, then try again.',
                          );
                        }).finally(() => setSubmitting(false));
                      }}
                    >
                      {submitting ? 'Continuing…' : 'Continue to console'}
                    </Button>
                  )}
                  <Button variant="text" onClick={() => navigate('/mfa/recovery', { state: locationState })}>
                    Use a recovery code instead
                  </Button>
                </>
              )}

              {mode === 'recovery' && (
                <>
                  <TextField
                    label="Recovery code"
                    value={recoveryCode}
                    onChange={(event) => setRecoveryCode(event.target.value)}
                    fullWidth
                    autoFocus
                  />
                  <Button
                    variant="contained"
                    fullWidth
                    disabled={submitting || !recoveryCode.trim()}
                    onClick={() => void handleVerifyRecovery()}
                    sx={{
                      py: 1.25,
                      fontWeight: 700,
                      background: GEMINI_GRADIENT,
                      '&:hover': { background: GEMINI_GRADIENT, opacity: 0.9 },
                    }}
                  >
                    {submitting ? 'Verifying…' : 'Verify recovery code'}
                  </Button>
                  {mfaStepVerified && (
                    <Button
                      variant="outlined"
                      fullWidth
                      disabled={submitting}
                      onClick={() => {
                        setSubmitting(true);
                        setError('');
                        void finishMfaChallengeLogin().catch((err) => {
                          setError(
                            mfaCompletionErrorMessage(err) ||
                              'Could not complete admin console sign-in. Check that admin-api is running, then try again.',
                          );
                        }).finally(() => setSubmitting(false));
                      }}
                    >
                      {submitting ? 'Continuing…' : 'Continue to console'}
                    </Button>
                  )}
                  <Button variant="text" onClick={() => navigate('/mfa/challenge', { state: locationState })}>
                    Back to authenticator code
                  </Button>
                </>
              )}

              {mode === 'enroll' && sessionMfaEnabled && (
                <>
                  <Alert severity="success" sx={{ borderRadius: 2 }}>
                    MFA is already enabled on this account. Continue by verifying an authenticator code.
                  </Alert>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate('/mfa/challenge', { state: locationState })}
                    sx={{
                      fontWeight: 700,
                      background: GEMINI_GRADIENT,
                      '&:hover': { background: GEMINI_GRADIENT, opacity: 0.9 },
                    }}
                  >
                    Go to MFA challenge
                  </Button>
                </>
              )}

              {mode === 'enroll' && !sessionMfaEnabled && setup && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Scan this QR code with Google Authenticator, Microsoft Authenticator, or a compatible TOTP app,
                    then enter the 6-digit code to finish enrollment.
                  </Typography>
                  <Box
                    component="img"
                    src={setup.qrCode}
                    alt="MFA QR code"
                    sx={{
                      width: 200,
                      height: 200,
                      mx: 'auto',
                      display: 'block',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                  <TextField
                    label="Authenticator code"
                    value={totpCode}
                    onChange={(event) => setTotpCode(normalizeCode(event.target.value))}
                    inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6 }}
                    fullWidth
                  />
                  <Button
                    variant="contained"
                    fullWidth
                    disabled={submitting || totpCode.length !== 6}
                    onClick={() => void handleEnableMfa()}
                    sx={{
                      py: 1.25,
                      fontWeight: 700,
                      background: GEMINI_GRADIENT,
                      '&:hover': { background: GEMINI_GRADIENT, opacity: 0.9 },
                    }}
                  >
                    {submitting ? 'Enabling MFA…' : 'Enable MFA'}
                  </Button>
                </>
              )}

              {mode === 'codes' && (
                <>
                  {recoveryCodes.length > 0 ? (
                    <Box
                      component="pre"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: 'action.hover',
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                        overflow: 'auto',
                      }}
                    >
                      {recoveryCodes.join('\n')}
                    </Box>
                  ) : (
                    <Alert severity="warning" sx={{ borderRadius: 2 }}>
                      No recovery codes were found. Return to sign in and complete MFA enrollment again.
                    </Alert>
                  )}
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      disabled={!recoveryCodes.length}
                      onClick={() => downloadRecoveryCodes(recoveryCodes)}
                    >
                      Download
                    </Button>
                    <Button
                      variant="contained"
                      fullWidth
                      disabled={submitting || !recoveryCodes.length || !credentials.email || !credentials.password}
                      onClick={() => void handleContinueFromCodes()}
                      sx={{
                        fontWeight: 700,
                        background: GEMINI_GRADIENT,
                        '&:hover': { background: GEMINI_GRADIENT, opacity: 0.9 },
                      }}
                    >
                      {submitting ? 'Continuing…' : 'Continue to console'}
                    </Button>
                  </Stack>
                </>
              )}

              <Divider />

              <Button variant="text" color="inherit" onClick={() => void handleBackToSignIn()}>
                Back to sign in
              </Button>
            </Stack>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
